// crewbit-admin/src/components/schedule/TravelSliver.tsx
//
// Schedule Module Phase C C1 — between-tile travel-time visualization for Day view.
// Spec: docs/references/schedule/SCHEDULE_MODULE_SPEC_v1.md §0.13 / §2.6.2 / D14.
//
// Body parallel to mobile's TravelSliver.tsx; admin uses Haversine fallback inline
// since the admin codebase doesn't share the mobile utils/driveTime.ts. Production
// uses Google Distance Matrix per Foundation v1 D14 footnote.

import { useMemo } from 'react';
import { palette, urgency } from '../../styles/tokens';
import type { ScheduleEntry } from '../../types/schedule';

const WARNING_BG = '#FEF3C7';

export interface TravelSliverProps {
  fromEntry: ScheduleEntry;
  toEntry: ScheduleEntry;
  travelBufferMinutes: number;
}

function haversineKm(lat1: number, lng1: number, lat2: number, lng2: number): number {
  const R = 6371;
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLng = ((lng2 - lng1) * Math.PI) / 180;
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos((lat1 * Math.PI) / 180) * Math.cos((lat2 * Math.PI) / 180) * Math.sin(dLng / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

function estimateDriveMinutes(lat1: number, lng1: number, lat2: number, lng2: number): number {
  return Math.max(1, Math.ceil(haversineKm(lat1, lng1, lat2, lng2) * 2));
}

export function TravelSliver({ fromEntry, toEntry, travelBufferMinutes }: TravelSliverProps) {
  const computed = useMemo(() => {
    const fLat = fromEntry.location_lat;
    const fLng = fromEntry.location_lng;
    const tLat = toEntry.location_lat;
    const tLng = toEntry.location_lng;
    if (fLat == null || fLng == null || tLat == null || tLng == null) {
      return { available: false as const };
    }
    const minutes = estimateDriveMinutes(fLat, fLng, tLat, tLng);
    const km = haversineKm(fLat, fLng, tLat, tLng);
    return {
      available: true as const,
      minutes,
      km: Math.round(km * 10) / 10,
      isWarning: minutes > travelBufferMinutes,
    };
  }, [fromEntry.location_lat, fromEntry.location_lng, toEntry.location_lat, toEntry.location_lng, travelBufferMinutes]);

  if (!computed.available) {
    return (
      <div style={{
        margin: '4px 12px', padding: '4px 12px', borderRadius: 8,
        backgroundColor: palette.g100, fontSize: 12, color: palette.g500,
      }}>
        Drive time unavailable
      </div>
    );
  }

  const sliverHeight = Math.max(8, Math.min(120, computed.minutes));

  return (
    <div
      aria-label={`travel time ${computed.minutes} minutes${computed.isWarning ? ' over buffer' : ''}`}
      style={{
        margin: '4px 12px',
        padding: '4px 12px',
        minHeight: sliverHeight,
        borderRadius: 8,
        backgroundColor: computed.isWarning ? WARNING_BG : palette.g100,
        display: 'flex',
        alignItems: 'center',
      }}
    >
      <span style={{
        fontSize: 12,
        color: computed.isWarning ? urgency.warning : palette.g600,
        fontWeight: computed.isWarning ? 600 : 400,
      }}>
        {computed.isWarning
          ? `⚠ ${computed.minutes} min · ${computed.km} km · over your ${travelBufferMinutes} min buffer`
          : `${computed.minutes} min · ${computed.km} km`}
      </span>
    </div>
  );
}
